import { jsx as _jsx } from "react/jsx-runtime";
/**
 * TranslatableThread
 *
 * A composition over kit `<MessageList>` (day-separator/skeleton/empty-state
 * rendering lives there) that adds per-inbound-message in-bubble translation.
 * On mount it batch-fetches all cached translations for the conversation in a
 * single GET (/api/conversations/:id/translations) and seeds the per-message
 * state.
 *
 * - Messages WITH a cached translation open showing the translation first.
 * - The in-bubble toggle switches between translation and original.
 * - Messages without a cached translation show «Перевести»; clicking it
 *   calls POST .../messages/:mid/translation and populates the bubble.
 *
 * The batch query is keyed by `conversationId`, so it refetches when the
 * conversation changes. The local per-message state (`translationMap` /
 * `showTranslated`), however, does NOT reset on its own — callers MUST mount
 * this with `key={conversationId}` so React remounts the instance per
 * conversation and clears that state (see ThreadView in inbox-page.tsx).
 */
import * as React from 'react';
import { useState, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from '@wit-support/ui';
import { MessageBubble, MessageList } from '@wit-support/ui-domain';
import { listConversationTranslations, translateMessage } from './api.js';
// ── poll-bound predicate ──────────────────────────────────────────────────────
/**
 * Window (ms) after an inbound message arrives during which we keep polling for
 * an auto-translation. Auto-translate (when enabled) fires off the inbound event
 * within seconds, so a couple of minutes comfortably covers the worker round-trip.
 */
export const TRANSLATION_POLL_RECENCY_MS = 2 * 60 * 1000; // 2 minutes
/**
 * Returns the refetch interval (ms) for the batch translations query, or `false`
 * to STOP polling.
 *
 * Polls (10s) ONLY while some inbound message both (a) lacks a cached translation
 * AND (b) is RECENT — i.e. arrived within the last {@link TRANSLATION_POLL_RECENCY_MS},
 * so the auto-translate worker (if enabled) could still write its translation any
 * moment. Once no recent-untranslated inbound remains — or there are no inbound
 * messages — returns false.
 *
 * This bounds the poll to the short post-arrival window instead of polling forever:
 * with auto-translate OFF (the default) inbound is never auto-translated, so an old
 * untranslated inbound would otherwise keep this query polling indefinitely. The
 * module can't see the autoTranslateInbound flag, so we gate on recency instead.
 *
 * Pure + exported so it can be unit-tested directly. `nowMs` is injectable for tests.
 */
export function translationPollInterval(messages, translatedItems, nowMs = Date.now()) {
    const translated = new Set(translatedItems.map((it) => it.messageId));
    const hasRecentUntranslatedInbound = messages.some((m) => {
        if (m.direction !== 'in' || translated.has(m.id))
            return false;
        const arrivedMs = Date.parse(m.createdAt);
        if (Number.isNaN(arrivedMs))
            return false;
        return nowMs - arrivedMs <= TRANSLATION_POLL_RECENCY_MS;
    });
    return hasRecentUntranslatedInbound ? 10_000 : false;
}
export function TranslatableThread({ conversationId, messages, isLoading = false, className, }) {
    // ── batch-fetch cached translations on mount (re-fetches when convo changes) ─
    const translationsQuery = useQuery({
        queryKey: ['conv-translations', conversationId],
        queryFn: () => listConversationTranslations(conversationId),
        // Poll on the same 10s cadence as the messages/inbox lists so a translation
        // written by the auto-translate worker for an inbound message that arrives
        // DURING the open session is picked up. Once it lands in `translationMap`,
        // that bubble defaults to translated at render time (unless the user has
        // explicitly toggled it to original via `showTranslated`).
        //
        // BOUND the poll to the useful window: keep polling (10s) only while some
        // INBOUND message still lacks a cached translation (i.e. something could yet
        // be auto-translated). Once every inbound message has a translation — or
        // there are no inbound messages — stop (return false). Avoids polling forever
        // when auto-translate is off / idle.
        refetchInterval: (q) => translationPollInterval(messages, q.state.data?.items ?? []),
        // A 503 (translation not configured) or network error must not crash the thread.
        retry: false,
    });
    // ── per-message translation map ───────────────────────────────────────────
    // Populated from the batch query; grows as the user triggers on-demand translates.
    const [translationMap, setTranslationMap] = useState(new Map());
    // Sync the batch fetch result into the map (runs once when data arrives).
    const batchItems = translationsQuery.data?.items;
    React.useEffect(() => {
        if (!batchItems || batchItems.length === 0)
            return;
        setTranslationMap((prev) => {
            // Guard: if no key/value actually changed, return the SAME reference so
            // React can bail out of the re-render (react-query issues a fresh array
            // reference on every 10s poll even when the payload is byte-identical).
            const next = new Map(prev);
            let changed = false;
            for (const item of batchItems) {
                if (prev.get(item.messageId) !== item.translatedText) {
                    next.set(item.messageId, item.translatedText);
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [batchItems]);
    // ── per-message showTranslated toggle state ───────────────────────────────
    // Holds ONLY explicit user toggles. The per-message default is derived AT
    // RENDER TIME from `translationMap` (see below): a message with a cached
    // translation shows translated on its FIRST commit (no flash of the original),
    // and an explicit entry here overrides that default. No seeding effect — a
    // post-render effect would render the original for one frame before flipping.
    const [showTranslated, setShowTranslated] = useState({});
    // ── on-demand translate mutation ──────────────────────────────────────────
    const [translatingId, setTranslatingId] = useState(null);
    const translateMut = useMutation({
        mutationFn: (messageId) => translateMessage(conversationId, messageId),
        onMutate: (messageId) => setTranslatingId(messageId),
        onSettled: () => setTranslatingId(null),
        onSuccess: (data, messageId) => {
            setTranslationMap((prev) => {
                const next = new Map(prev);
                next.set(messageId, data.translatedText);
                return next;
            });
            setShowTranslated((prev) => ({ ...prev, [messageId]: true }));
        },
        // Surface a failed on-demand translate (503 / LLM error / network) — without
        // this the button silently snaps back to «Перевести» with no feedback. Same
        // pattern as reply-composer / queue-panel.
        onError: (err) => toast.error(err.message),
    });
    const handleToggle = useCallback((messageId, next) => {
        setShowTranslated((prev) => ({ ...prev, [messageId]: next }));
    }, []);
    const handleTranslate = useCallback((messageId) => {
        translateMut.mutate(messageId);
    }, [translateMut]);
    // ── render ────────────────────────────────────────────────────────────────
    return (_jsx(MessageList, { messages: messages, isLoading: isLoading, className: className, leading: translationsQuery.isError ? (_jsx("p", { role: "status", className: "rounded-[var(--radius-2)] bg-[var(--color-surface-sunken)] px-3 py-1.5 text-xs text-[var(--color-text-muted)]", children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0437\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u043F\u0435\u0440\u0435\u0432\u043E\u0434\u044B" })) : null, renderMessage: (m) => {
            // Extracted once so the translation lookup / existence check share the
            // single `Map.get` result instead of dueling `get`+`has` calls.
            const cachedText = translationMap.get(m.id);
            return m.direction === 'in' ? (_jsx(MessageBubble, { message: m, translation: cachedText ? { text: cachedText } : null, 
                // Default to translated when a translation exists (no first-frame flash
                // of the original); an explicit user toggle in `showTranslated` overrides.
                showTranslated: showTranslated[m.id] ?? cachedText !== undefined, onToggleTranslated: (next) => handleToggle(m.id, next), onTranslate: () => handleTranslate(m.id), translating: translateMut.isPending && translatingId === m.id, canTranslate: !!(m.bodyText || m.bodyHtml) })) : (_jsx(MessageBubble, { message: m }));
        } }));
}
//# sourceMappingURL=translatable-thread.js.map